/**
 * Barra tipo Looker: archivo, insertar, recurso (fuentes), cliente/proyecto.
 */
import React, { useEffect, useRef } from 'react';
import ReportPageNav from './ReportPageNav';
import {
    CHART_WIDGET_TYPES,
    CONTROL_WIDGET_TYPES,
    INSERT_WIDGET_TYPES
} from './engineConfig';

const MENUS = [
    { key: 'archivo', label: 'Archivo' },
    { key: 'editar', label: 'Editar' },
    { key: 'vista', label: 'Vista' },
    { key: 'insertar', label: 'Insertar' },
    { key: 'pagina', label: 'Página' },
    { key: 'recurso', label: 'Recurso' }
];

const Icon = ({ children, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {children}
    </svg>
);

const CreatorStudioBar = ({
    openMenu,
    onToggleMenu,
    onCloseMenus,
    reportName,
    onReportNameChange,
    clients = [],
    projects = [],
    selectedClientId,
    selectedProjectId,
    onClientChange,
    onProjectChange,
    customReports = [],
    editingReportId,
    onLoadReport,
    pages = [],
    activePageId,
    onSelectPage,
    onAddPage,
    onDeletePage,
    onRenamePage,
    onDuplicatePage,
    onAddSection,
    pageOutlineOpen,
    onTogglePageOutline,
    onOpenPageOutline,
    dataSourceCount = 0,
    canUndo,
    onUndo,
    canRedo,
    onRedo,
    dirty,
    onShowShortcuts,
    onPreview,
    onSaveDraft,
    onPublish,
    onGoHome,
    readOnly = false,
    savingReport,
    autoSaving,
    autoSaveEnabled,
    autoSaveMinutes,
    autoSaveChoices = [1, 2, 5, 10, 15, 30],
    lastSavedAt,
    onAutoSaveEnabledChange,
    onAutoSaveMinutesChange,
    canPreview,
    onAddData,
    onOpenDrive,
    onBlend,
    onRefreshSources,
    refreshingSources,
    onAddWidget,
    onTheme,
    onResetFilters,
    paletteOpen,
    propsOpen,
    onTogglePalette,
    onToggleProps,
    selectedId,
    selectedCount,
    selectedIsContainer,
    hasGroup,
    onDuplicateWidget,
    onPack,
    onUnpack,
    onGroup,
    onUngroup,
    canArrange,
    onLayer,
    onAlign
}) => {
    const rootRef = useRef(null);
    const dropdownOpen = MENUS.some(item => item.key === openMenu) || openMenu === 'chart' || openMenu === 'control';

    useEffect(() => {
        if (!dropdownOpen) return undefined;
        const close = (event) => {
            const tag = event.target?.tagName;
            if (tag === 'SELECT' || tag === 'OPTION') return;
            if (rootRef.current && !rootRef.current.contains(event.target)) onCloseMenus();
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [dropdownOpen, onCloseMenus]);

    const sourceHint = dataSourceCount
        ? `${dataSourceCount} fuente${dataSourceCount === 1 ? '' : 's'}`
        : 'Sin datos';

    const MenuItem = ({ disabled, onClick, children, danger, keepOpen }) => (
        <button
            type="button"
            disabled={disabled}
            style={{
                ...styles.menuItem,
                ...(danger ? styles.menuItemDanger : {}),
                ...(disabled ? styles.menuItemOff : {})
            }}
            onClick={() => {
                if (disabled) return;
                onClick();
                if (!keepOpen) onCloseMenus();
            }}
        >
            {children}
        </button>
    );

    return (
        <div ref={rootRef} style={styles.chrome}>
            <div style={styles.header}>
                <div style={styles.brand}>
                    <button
                        type="button"
                        style={styles.logo}
                        title={onGoHome ? 'Inicio del Creador' : 'Creador'}
                        onClick={onGoHome || undefined}
                        disabled={!onGoHome}
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                            <circle cx="7" cy="8" r="3.2" fill="#1a73e8" />
                            <circle cx="17" cy="8" r="3.2" fill="#34a853" />
                            <circle cx="12" cy="16.5" r="3.2" fill="#5E35B1" />
                        </svg>
                    </button>
                    <div style={styles.titleBlock}>
                        <input
                            value={reportName}
                            onChange={e => onReportNameChange(e.target.value)}
                            placeholder="Nombre del reporte"
                            style={styles.titleInput}
                        />
                        <div style={styles.metaRow}>
                            <select
                                value={selectedClientId}
                                onChange={e => onClientChange(e.target.value)}
                                onMouseDown={e => e.stopPropagation()}
                                style={{
                                    ...styles.metaSelect,
                                    ...(!selectedClientId ? styles.metaSelectWarn : {})
                                }}
                                title="Cliente"
                            >
                                <option value="">Cliente</option>
                                {clients.map(client => (
                                    <option key={client.id} value={client.id}>{client.nombre}</option>
                                ))}
                            </select>
                            <select
                                value={selectedProjectId}
                                onChange={e => onProjectChange(e.target.value)}
                                onMouseDown={e => e.stopPropagation()}
                                disabled={!selectedClientId}
                                style={{
                                    ...styles.metaSelect,
                                    ...(!selectedProjectId ? styles.metaSelectWarn : {})
                                }}
                                title="Proyecto"
                            >
                                <option value="">{selectedClientId ? 'Proyecto' : 'Elige un cliente'}</option>
                                {projects.filter(project => project.clientId === selectedClientId).map(project => (
                                    <option key={project.id} value={project.id}>{project.nombre}</option>
                                ))}
                            </select>
                            <span style={styles.metaLine}>{sourceHint}</span>
                        </div>
                    </div>
                </div>

                <nav style={styles.menus}>
                    {MENUS.map(item => (
                        <div key={item.key} style={styles.menuWrap}>
                            <button
                                type="button"
                                style={openMenu === item.key ? styles.menuBtnOn : styles.menuBtn}
                                onClick={() => onToggleMenu(item.key)}
                            >
                                {item.label}
                            </button>
                            {openMenu === item.key && item.key === 'archivo' && (
                                <div style={styles.dropdown}>
                                    {onGoHome && <MenuItem onClick={onGoHome}>Inicio del Creador</MenuItem>}
                                    <MenuItem onClick={() => onLoadReport('')}>Nuevo dashboard</MenuItem>
                                    <div style={styles.menuLabel}>Abrir</div>
                                    {customReports.length === 0 && <div style={styles.menuEmpty}>No hay dashboards guardados</div>}
                                    {customReports.map(report => (
                                        <MenuItem key={report.id} onClick={() => onLoadReport(report.id)}>
                                            {report.isDraft ? '[Borrador] ' : ''}{report.reporteNombre}
                                            {report.id === editingReportId ? ' ✓' : ''}
                                        </MenuItem>
                                    ))}
                                    <div style={styles.menuSep} />
                                    <MenuItem disabled={readOnly} onClick={onSaveDraft}>{savingReport ? 'Guardando…' : 'Guardar borrador'}</MenuItem>
                                    <MenuItem disabled={readOnly} onClick={onPublish}>{savingReport ? 'Guardando…' : 'Publicar'}</MenuItem>
                                    <div style={styles.menuSep} />
                                    <div style={styles.menuLabel}>Autoguardado</div>
                                    <MenuItem keepOpen onClick={() => onAutoSaveEnabledChange?.(!autoSaveEnabled)}>
                                        {autoSaveEnabled ? 'Activado' : 'Desactivado'}
                                    </MenuItem>
                                    {autoSaveEnabled && (
                                        <label style={styles.autoSaveRow}>
                                            Cada
                                            <select
                                                value={autoSaveMinutes}
                                                onChange={e => onAutoSaveMinutesChange?.(Number(e.target.value))}
                                                onMouseDown={e => e.stopPropagation()}
                                                style={styles.autoSaveSelect}
                                            >
                                                {autoSaveChoices.map(minutes => (
                                                    <option key={minutes} value={minutes}>{minutes} min</option>
                                                ))}
                                            </select>
                                        </label>
                                    )}
                                </div>
                            )}
                            {openMenu === item.key && item.key === 'editar' && (
                                <div style={styles.dropdown}>
                                    <MenuItem disabled={!canUndo} onClick={onUndo}>Deshacer</MenuItem>
                                    <MenuItem disabled={!canRedo} onClick={onRedo}>Rehacer</MenuItem>
                                    {onShowShortcuts && <MenuItem onClick={onShowShortcuts}>Atajos de teclado</MenuItem>}
                                    <MenuItem disabled={!selectedId} onClick={() => onDuplicateWidget(selectedId)}>Duplicar elemento</MenuItem>
                                    <div style={styles.menuSep} />
                                    <MenuItem disabled={selectedCount < 1} onClick={onPack}>Crear bloque</MenuItem>
                                    <MenuItem disabled={!selectedIsContainer} onClick={onUnpack}>Desarmar bloque</MenuItem>
                                    <MenuItem disabled={selectedCount < 2} onClick={onGroup}>Agrupar</MenuItem>
                                    <MenuItem disabled={!hasGroup} onClick={onUngroup}>Desagrupar</MenuItem>
                                    <div style={styles.menuSep} />
                                    <div style={styles.menuLabel}>Orden</div>
                                    <MenuItem disabled={!canArrange} onClick={() => onLayer('front')}>Traer al frente</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onLayer('forward')}>Adelante</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onLayer('backward')}>Atrás</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onLayer('back')}>Enviar al fondo</MenuItem>
                                    <div style={styles.menuLabel}>Alinear</div>
                                    <MenuItem disabled={!canArrange} onClick={() => onAlign('top')}>Arriba</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onAlign('middle')}>Centro vertical</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onAlign('bottom')}>Abajo</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onAlign('left')}>Izquierda</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onAlign('center')}>Centro horizontal</MenuItem>
                                    <MenuItem disabled={!canArrange} onClick={() => onAlign('right')}>Derecha</MenuItem>
                                </div>
                            )}
                            {openMenu === item.key && item.key === 'vista' && (
                                <div style={styles.dropdown}>
                                    <MenuItem disabled={!canPreview} onClick={onPreview}>Ver como usuario</MenuItem>
                                    <MenuItem onClick={onTogglePalette}>{paletteOpen ? 'Ocultar campos' : 'Mostrar campos'}</MenuItem>
                                    <MenuItem onClick={onToggleProps}>{propsOpen ? 'Ocultar propiedades' : 'Mostrar propiedades'}</MenuItem>
                                    <MenuItem onClick={onTheme}>Tema y diseño</MenuItem>
                                </div>
                            )}
                            {openMenu === item.key && item.key === 'insertar' && (
                                <div style={{ ...styles.dropdown, minWidth: '220px' }}>
                                    <div style={styles.menuLabel}>Gráficos</div>
                                    {CHART_WIDGET_TYPES.map(widget => (
                                        <MenuItem key={widget.type} onClick={() => onAddWidget(widget.type)}>{widget.label}</MenuItem>
                                    ))}
                                    <div style={styles.menuLabel}>Controles</div>
                                    {CONTROL_WIDGET_TYPES.map(widget => (
                                        <MenuItem key={widget.type} onClick={() => onAddWidget(widget.type)}>{widget.label}</MenuItem>
                                    ))}
                                    <div style={styles.menuLabel}>Elementos</div>
                                    {INSERT_WIDGET_TYPES.map(widget => (
                                        <MenuItem key={widget.type} onClick={() => onAddWidget(widget.type)}>{widget.label}</MenuItem>
                                    ))}
                                </div>
                            )}
                            {openMenu === item.key && item.key === 'pagina' && (
                                <div style={styles.dropdown}>
                                    {pages.map(page => (
                                        <MenuItem key={page.id} onClick={() => onSelectPage(page.id)}>
                                            {page.id === activePageId ? '● ' : ''}{page.name}
                                        </MenuItem>
                                    ))}
                                    <div style={styles.menuSep} />
                                    <MenuItem onClick={onTogglePageOutline}>
                                        {pageOutlineOpen ? 'Ocultar índice de hojas' : 'Organizar hojas y secciones'}
                                    </MenuItem>
                                    <MenuItem onClick={onAddPage}>Nueva hoja</MenuItem>
                                    <MenuItem onClick={onAddSection}>Nueva sección</MenuItem>
                                    <MenuItem onClick={onDuplicatePage}>Duplicar hoja</MenuItem>
                                    <MenuItem danger disabled={pages.length <= 1} onClick={() => onDeletePage(activePageId)}>Eliminar hoja</MenuItem>
                                </div>
                            )}
                            {openMenu === item.key && item.key === 'recurso' && (
                                <div
                                    style={{ ...styles.dropdown, minWidth: '260px', padding: '10px' }}
                                    onMouseDown={e => e.stopPropagation()}
                                >
                                    <div style={styles.menuEmpty}>
                                        Cliente y proyecto se eligen junto al título, arriba a la izquierda.
                                    </div>
                                    <div style={styles.menuSep} />
                                    <MenuItem keepOpen onClick={onAddData}>Gestionar fuentes de datos</MenuItem>
                                    <MenuItem keepOpen onClick={onOpenDrive}>Buscar en Drive</MenuItem>
                                    <MenuItem keepOpen onClick={onBlend}>Combinar fuentes</MenuItem>
                                    {onRefreshSources && (
                                        <>
                                            <div style={styles.menuSep} />
                                            <MenuItem
                                                disabled={refreshingSources || !dataSourceCount}
                                                onClick={onRefreshSources}
                                            >
                                                {refreshingSources ? 'Actualizando datos…' : 'Actualizar datos de Drive'}
                                            </MenuItem>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                <div style={styles.actions}>
                    <button type="button" style={styles.resetBtn} onClick={onResetFilters} title="Quitar filtros aplicados">
                        Restablecer
                    </button>
                    <button type="button" style={styles.draftBtn} disabled={savingReport || readOnly} onClick={onSaveDraft}>
                        {savingReport ? 'Guardando…' : 'Borrador'}
                    </button>
                    <span
                        style={dirty && !autoSaving ? { ...styles.saveHint, ...styles.saveHintDirty } : styles.saveHint}
                        title={`${dirty ? 'Hay cambios sin guardar (Ctrl+S para guardar). ' : ''}${autoSaveEnabled ? `Autoguardado cada ${autoSaveMinutes} min` : 'Autoguardado desactivado'}`}
                    >
                        {autoSaving
                            ? 'Autoguardando…'
                            : dirty
                                ? '● Sin guardar'
                                : lastSavedAt
                                ? `Guardado ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                : (autoSaveEnabled ? `Auto ${autoSaveMinutes} min` : 'Sin auto')}
                    </span>
                    <button type="button" style={styles.viewBtn} disabled={!canPreview} onClick={onPreview}>
                        <Icon size={16}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></Icon>
                        Ver
                    </button>
                    <button type="button" style={styles.publishBtn} disabled={savingReport || readOnly} onClick={onPublish} title={readOnly ? 'Solo lectura' : undefined}>
                        {savingReport ? 'Guardando…' : 'Publicar'}
                    </button>
                </div>
            </div>

            <div style={styles.toolbar}>
                <button type="button" style={styles.toolBtn} disabled={!canUndo} onClick={onUndo} title="Deshacer (Ctrl+Z)">
                    <Icon><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></Icon>
                </button>
                {onRedo && (
                    <button type="button" style={styles.toolBtn} disabled={!canRedo} onClick={onRedo} title="Rehacer (Ctrl+Shift+Z o Ctrl+Y)">
                        <Icon><polyline points="15 14 20 9 15 4" /><path d="M4 20v-7a4 4 0 0 1 4-4h12" /></Icon>
                    </button>
                )}

                <span style={styles.vsep} />

                <ReportPageNav
                    compact
                    pages={pages}
                    activePageId={activePageId}
                    onSelect={onSelectPage}
                    editable
                    onAdd={onAddPage}
                    onDelete={onDeletePage}
                    onRename={onRenamePage}
                    onDuplicate={onDuplicatePage}
                    onOpenOutline={() => {
                        if (onOpenPageOutline) onOpenPageOutline();
                    }}
                />

                <span style={styles.vsep} />

                <button
                    type="button"
                    style={openMenu === 'source' ? styles.toolBtnOn : styles.toolBtn}
                    onClick={onAddData}
                    title="Añadir o editar fuentes de Google Sheets"
                >
                    <Icon><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" /></Icon>
                    <span style={styles.toolLabel}>Añadir datos</span>
                </button>
                <button
                    type="button"
                    style={styles.toolBtn}
                    onClick={onBlend}
                    title="Combinar fuentes con un join"
                >
                    <Icon><circle cx="9" cy="12" r="5" /><circle cx="15" cy="12" r="5" /></Icon>
                    <span style={styles.toolLabel}>Combinar</span>
                </button>

                <span style={styles.vsep} />

                <div style={styles.menuWrap}>
                    <button
                        type="button"
                        style={openMenu === 'chart' ? styles.toolBtnOn : styles.toolBtn}
                        onClick={() => onToggleMenu('chart')}
                        title="Añadir un gráfico"
                    >
                        <Icon><rect x="3" y="12" width="4" height="8" /><rect x="10" y="8" width="4" height="12" /><rect x="17" y="4" width="4" height="16" /></Icon>
                        <span style={styles.toolLabel}>Añadir un gráfico</span>
                        <span style={styles.caret}>▾</span>
                    </button>
                    {openMenu === 'chart' && (
                        <div style={{ ...styles.dropdown, top: 'calc(100% + 6px)' }}>
                            {CHART_WIDGET_TYPES.map(widget => (
                                <MenuItem key={widget.type} onClick={() => onAddWidget(widget.type)}>{widget.label}</MenuItem>
                            ))}
                        </div>
                    )}
                </div>

                <div style={styles.menuWrap}>
                    <button
                        type="button"
                        style={openMenu === 'control' ? styles.toolBtnOn : styles.toolBtn}
                        onClick={() => onToggleMenu('control')}
                        title="Añadir un control"
                    >
                        <Icon><rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" /><circle cx="9" cy="7" r="1.4" fill="currentColor" /><circle cx="15" cy="17" r="1.4" fill="currentColor" /></Icon>
                        <span style={styles.toolLabel}>Añadir un control</span>
                        <span style={styles.caret}>▾</span>
                    </button>
                    {openMenu === 'control' && (
                        <div style={{ ...styles.dropdown, top: 'calc(100% + 6px)' }}>
                            {CONTROL_WIDGET_TYPES.map(widget => (
                                <MenuItem key={widget.type} onClick={() => onAddWidget(widget.type)}>{widget.label}</MenuItem>
                            ))}
                        </div>
                    )}
                </div>

                {INSERT_WIDGET_TYPES.map(widget => (
                    <button
                        key={widget.type}
                        type="button"
                        style={styles.iconOnly}
                        title={widget.label}
                        onClick={() => onAddWidget(widget.type)}
                    >
                        {widget.type === 'text' && <span style={styles.letter}>A</span>}
                        {widget.type === 'divider' && <Icon><line x1="4" y1="12" x2="20" y2="12" /></Icon>}
                        {widget.type === 'container' && <Icon><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /></Icon>}
                    </button>
                ))}

                <span style={styles.vsep} />

                <button type="button" style={styles.linkBtn} onClick={onTheme}>
                    Tema y diseño
                </button>
            </div>
        </div>
    );
};

const styles = {
    chrome: {
        background: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '12px',
        overflow: 'visible',
        flexShrink: 0
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 12px 6px',
        borderBottom: '1px solid #e8eaed',
        flexWrap: 'wrap'
    },
    brand: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: '1 1 220px' },
    logo: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: '#f8f9fa',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    titleBlock: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
    titleInput: {
        border: 'none',
        background: 'transparent',
        fontSize: '1.05rem',
        fontWeight: 600,
        color: '#202124',
        padding: '2px 4px',
        minWidth: 0,
        width: '100%',
        outline: 'none'
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        paddingLeft: '4px',
        flexWrap: 'wrap'
    },
    metaSelect: {
        maxWidth: '160px',
        border: '1px solid #dadce0',
        background: '#fff',
        borderRadius: '6px',
        padding: '3px 6px',
        fontSize: '0.72rem',
        color: '#202124',
        cursor: 'pointer'
    },
    metaSelectWarn: {
        border: '1px solid #e37400',
        background: '#fff8e1',
        color: '#e37400'
    },
    metaLine: {
        fontSize: '0.7rem',
        color: '#5f6368',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    menus: { display: 'flex', alignItems: 'center', gap: '2px', flexWrap: 'wrap' },
    menuWrap: { position: 'relative' },
    menuBtn: {
        border: 'none',
        background: 'transparent',
        color: '#3c4043',
        fontSize: '0.82rem',
        padding: '6px 10px',
        borderRadius: '6px',
        cursor: 'pointer'
    },
    menuBtnOn: {
        border: 'none',
        background: '#e8f0fe',
        color: '#174ea6',
        fontSize: '0.82rem',
        padding: '6px 10px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 600
    },
    dropdown: {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        zIndex: 40,
        minWidth: '200px',
        background: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(60,64,67,0.18)',
        padding: '6px',
        display: 'flex',
        flexDirection: 'column'
    },
    menuItem: {
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        padding: '7px 10px',
        borderRadius: '6px',
        fontSize: '0.82rem',
        color: '#202124',
        cursor: 'pointer'
    },
    menuItemDanger: { color: '#c5221f' },
    menuItemOff: { color: '#9aa0a6', cursor: 'default' },
    menuLabel: {
        fontSize: '0.68rem',
        fontWeight: 700,
        color: '#5f6368',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '8px 10px 4px'
    },
    menuEmpty: { fontSize: '0.78rem', color: '#9aa0a6', padding: '6px 10px' },
    menuSep: { height: '1px', background: '#e8eaed', margin: '6px 4px' },
    autoSaveRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px 10px',
        fontSize: '0.82rem',
        color: '#202124'
    },
    autoSaveSelect: {
        padding: '5px 8px',
        borderRadius: '8px',
        border: '1px solid #dadce0',
        fontSize: '0.82rem',
        background: '#fff'
    },
    saveHint: {
        fontSize: '0.72rem',
        color: '#5f6368',
        whiteSpace: 'nowrap',
        padding: '0 4px'
    },
    saveHintDirty: { color: '#b06000', fontWeight: 600 },
    fieldLabel: { fontSize: '0.72rem', fontWeight: 700, color: '#5f6368', margin: '4px 2px' },
    field: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '7px 8px',
        borderRadius: '8px',
        border: '1px solid #dadce0',
        fontSize: '0.82rem',
        marginBottom: '6px',
        background: '#fff'
    },
    actions: { display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', flexWrap: 'wrap' },
    resetBtn: {
        border: '1px solid #dadce0',
        background: '#fff',
        color: '#3c4043',
        borderRadius: '18px',
        padding: '6px 12px',
        fontSize: '0.78rem',
        cursor: 'pointer'
    },
    draftBtn: {
        border: '1px solid #d2e3fc',
        background: '#e8f0fe',
        color: '#174ea6',
        borderRadius: '18px',
        padding: '6px 14px',
        fontSize: '0.78rem',
        fontWeight: 700,
        cursor: 'pointer'
    },
    viewBtn: {
        border: 'none',
        background: '#1a73e8',
        color: '#fff',
        borderRadius: '18px',
        padding: '6px 14px',
        fontSize: '0.78rem',
        fontWeight: 700,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
    },
    publishBtn: {
        border: 'none',
        background: '#5E35B1',
        color: '#fff',
        borderRadius: '18px',
        padding: '6px 14px',
        fontSize: '0.78rem',
        fontWeight: 700,
        cursor: 'pointer'
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 10px',
        flexWrap: 'wrap',
        background: '#fff'
    },
    toolBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: 'none',
        background: 'transparent',
        color: '#3c4043',
        borderRadius: '8px',
        padding: '6px 8px',
        cursor: 'pointer',
        fontSize: '0.78rem',
        fontWeight: 600
    },
    toolBtnOn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: 'none',
        background: '#e8f0fe',
        color: '#174ea6',
        borderRadius: '8px',
        padding: '6px 8px',
        cursor: 'pointer',
        fontSize: '0.78rem',
        fontWeight: 600
    },
    toolLabel: { whiteSpace: 'nowrap' },
    caret: { fontSize: '0.65rem', color: '#5f6368' },
    iconOnly: {
        width: '34px',
        height: '34px',
        border: 'none',
        background: 'transparent',
        color: '#3c4043',
        borderRadius: '8px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    letter: { fontFamily: 'Georgia, serif', fontSize: '1.05rem', fontWeight: 700 },
    linkBtn: {
        border: 'none',
        background: 'transparent',
        color: '#1a73e8',
        fontSize: '0.78rem',
        fontWeight: 700,
        cursor: 'pointer',
        padding: '6px 8px'
    },
    vsep: {
        width: '1px',
        height: '22px',
        background: '#dadce0',
        margin: '0 4px',
        flexShrink: 0
    }
};

export default CreatorStudioBar;
